import React, { useEffect, useRef, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

// Roomy centered editor for a single cell value, opened from the bulk-edit grid
// when a field is awkward to edit inline. `editor.mode` picks the input:
//   "rich"  -> react-quill (descriptions; HTML, formatting preserved)
//   "plain" -> wrapping textarea (names; plain text, Enter applies)
// It holds its OWN draft state, so typing here never re-renders the grid — the
// value is written back to the row only on Apply. Mounted only while open.
export default function CellEditorModal({ editor, onApply, onClose }) {
  const [draft, setDraft] = useState(editor.value ?? "");
  const draftRef = useRef(draft);
  draftRef.current = draft;

  // Esc cancels (discard); Ctrl/Cmd+Enter applies. onApply/onClose are stable
  // enough; the listener is capture-phase so it wins before field handlers.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      } else if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault();
        onApply(draftRef.current);
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [onApply, onClose]);

  const isRich = editor.mode === "rich";

  return (
    <div className="modal-backdrop-bulk">
      <div
        className="bulk-desc-modal"
        role="dialog"
        aria-modal="true"
        aria-label={`Edit ${editor.label}`}
      >
        <div className="bulk-desc-modal__head">
          <div>
            <h5 className="bulk-desc-modal__title">{editor.label}</h5>
            <small className="bulk-desc-modal__hint">
              {isRich
                ? "Rich text — formatting is preserved. Esc to cancel, Ctrl/⌘+Enter to apply."
                : "Esc to cancel, Enter or Ctrl/⌘+Enter to apply."}
            </small>
          </div>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={onClose}
          />
        </div>

        <div
          className={`bulk-desc-editor${editor.rtl ? " is-rtl" : ""}${
            isRich ? "" : " bulk-desc-editor--plain"
          }`}
        >
          {isRich ? (
            <ReactQuill theme="snow" value={draft} onChange={setDraft} />
          ) : (
            <textarea
              className="bulk-text-area"
              value={draft}
              autoFocus
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                // Names are single-line: plain Enter applies (no newline).
                // Ctrl/Cmd+Enter is handled by the document listener above.
                if (
                  e.key === "Enter" &&
                  !e.shiftKey &&
                  !e.ctrlKey &&
                  !e.metaKey
                ) {
                  e.preventDefault();
                  onApply(draft);
                }
              }}
            />
          )}
        </div>

        <div className="bulk-desc-modal__foot">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => onApply(draft)}
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}
